import { FaCheckCircle, FaTimesCircle, FaExclamationTriangle, FaNetworkWired } from 'react-icons/fa';

/**
 * The Vocus vendor-portal "NBN Availability" page, rebuilt from our own SQ
 * response so staff don't have to log into the Vocus portal to read a
 * qualification.
 *
 * Everything here comes from `qualifyDetail` (vocusBridge.parseQualifyDetail) —
 * which the lambda ONLY returns to a verified admin. A caller without a Bearer
 * token gets the sanitised public payload and `detail` will be null.
 *
 * Two fields the vendor portal shows are NOT in the WSM SQ response and are
 * derived here — both are labelled in the UI so nobody mistakes them for
 * vendor-asserted values:
 *   • Max Available Speed  — the widest down/up range across the returned
 *                            copper pairs (or NTD capacity on FTTP).
 *   • Min ThroughPut       — not returned at all; shown only if a param appears.
 */

const TECH_NAMES = {
  FTTP: 'Fibre to the Premises',
  FTTC: 'Fibre to the Curb',
  FTTN: 'Fibre to the Node',
  FTTB: 'Fibre to the Building',
  HFC: 'Hybrid Fibre Coaxial',
  FW: 'Fixed Wireless',
  WIRELESS: 'Fixed Wireless',
  SAT: 'Satellite',
  SATELLITE: 'Satellite',
};

const techCode = (detail, result) => {
  const raw = String(
    detail?.serviceType || result?.accessTechnology || result?.technology || ''
  ).toUpperCase();
  const hit = Object.keys(TECH_NAMES).find((k) => raw.includes(k));
  // Vocus returns the product family "FIBRE" on FTTP; the portal shows FTTP.
  return hit || (raw === 'FIBRE' ? 'FTTP' : raw || null);
};

/** "20210212" / "2021-02-12" → "12-02-2021", the vendor portal's format. */
const fmtDate = (v) => {
  const s = String(v ?? '').trim();
  if (!s) return null;
  const compact = s.match(/^(\d{4})(\d{2})(\d{2})$/);
  if (compact) return `${compact[3]}-${compact[2]}-${compact[1]}`;
  const dashed = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (dashed) return `${dashed[3]}-${dashed[2]}-${dashed[1]}`;
  return s;
};

/** Widest speed range across records, e.g. ["25-75","50-100"] → "25-100". */
const widestRange = (values) => {
  const nums = [];
  for (const v of values) {
    for (const m of String(v ?? '').matchAll(/\d+(?:\.\d+)?/g)) nums.push(parseFloat(m[0]));
  }
  if (nums.length === 0) return null;
  const lo = Math.min(...nums);
  const hi = Math.max(...nums);
  return lo === hi ? `${hi}Mbps` : `${lo}-${hi}Mbps`;
};

function Row({ k, v, tone, hint }) {
  const toneClass =
    tone === 'good' ? 'text-[#b8bb26] font-semibold'
      : tone === 'warn' ? 'text-[#fb4934]'
      : tone === 'muted' ? 'text-[#928374]'
      : 'text-[#ebdbb2]';
  return (
    <div className="flex gap-4 py-1.5 border-b border-[#3c3836]/40 last:border-b-0">
      <div className="w-52 shrink-0 text-sm text-[#a89984]">
        {k}
        {hint && <span className="ml-1 text-[10px] text-[#665c54]" title={hint}>ⓘ</span>}
      </div>
      <div className={`text-sm min-w-0 break-words ${toneClass}`}>{v ?? '—'}</div>
    </div>
  );
}

function PortPill({ port }) {
  const ok = port.available === true;
  return (
    <span
      title={[port.portLabel, port.epid ? `EPID ${port.epid}` : null, port.mac].filter(Boolean).join(' · ')}
      className={`inline-block mr-1 mb-1 px-2 py-0.5 rounded text-[11px] font-mono ${
        ok ? 'bg-[#b8bb26]/20 text-[#b8bb26] border border-[#b8bb26]/40'
           : 'bg-[#d79921]/20 text-[#d79921] border border-[#d79921]/40'
      }`}
    >
      {port.portName || `UNI-D${port.portNumber ?? '?'}`}
    </span>
  );
}

export default function NbnAvailabilityResult({ detail, result, locationId, addressLong }) {
  if (!detail) return null;

  const tech = techCode(detail, result);
  const pass = (detail.result || result?.status) === 'PASS';
  const pairs = detail.copperPairs || [];
  const ntds = detail.ntds || [];
  const allPorts = [...ntds.flatMap((n) => n.ports || []), ...(detail.orphanPorts || [])];

  // Max Available Speed — copper-pair ranges on FTTC/FTTN/FTTB; NTD capacity on FTTP.
  // Only pairs we could actually sell count: a pair already carrying someone else's
  // NBN service ("Line In Use") is usually the fastest one at the premise, and
  // including it would quote a speed we can't order. The vendor portal does the same.
  const isInUse = (c) => /in use|active/i.test(String(c.nbnServiceStatus || ''));
  const sellablePairs = pairs.filter((c) => !isInUse(c));
  const speedPairs = sellablePairs.length > 0 ? sellablePairs : pairs;
  const maxDown = pairs.length
    ? widestRange(speedPairs.map((c) => c.downloadSpeed))
    : widestRange(ntds.map((n) => n.downloadSpeed));
  const maxUp = pairs.length
    ? widestRange(speedPairs.map((c) => c.uploadSpeed))
    : widestRange(ntds.map((n) => n.uploadSpeed));
  const maxSpeed = maxDown || maxUp ? `${maxDown || '—'} / ${maxUp || '—'}` : null;

  // Min ThroughPut is not a WSM SQ param — surface it only if one turns up.
  const params = result?.params || {};
  const minThroughput = params.MinThroughput || params.MinThroughPut || params.MinimumThroughput || null;

  // ExtraCharge on a CopperPairRecord is Vocus's subsequent-install flag.
  const chargedPairs = pairs.filter((c) => c.extraCharge === true);
  const inUsePairs = pairs.filter(isInUse);
  const subsequentInstall = pairs.length === 0
    ? null
    : chargedPairs.length > 0
      ? {
          text: `Applicable.${inUsePairs.length ? ' An existing NBN active service is detected.' : ''} (ExtraCharge on ${chargedPairs.map((c) => c.copperPairId).join(', ')})`,
          tone: 'warn',
        }
      : { text: 'Not applicable — no copper pair returned ExtraCharge.', tone: undefined };

  const higherSpeedTier = (detail.features || [])
    .filter((f) => /speed|tier/i.test(String(f.name || '')))
    .map((f) => `${f.name}: ${f.value}`)
    .join(' · ');

  const altTech = detail.alternativeTechnology == null
    ? null
    : detail.alternativeTechnology
      ? `Supported${detail.coat?.forecastAlternativeTechnology ? ` — ${detail.coat.forecastAlternativeTechnology}` : ''}`
      : 'Fibre On Demand Not Supported';

  return (
    <div className="space-y-4">
      {/* ── Result header — the vendor portal's two-column summary ── */}
      <div className="bg-[#282828] border border-[#3c3836] rounded-xl overflow-hidden">
        <div className="px-5 py-3 bg-[#1d2021] border-b border-[#3c3836] flex items-center justify-between flex-wrap gap-2">
          <h2 className="text-base font-semibold text-[#ebdbb2] flex items-center gap-2">
            {pass ? <FaCheckCircle className="text-[#b8bb26]" /> : <FaTimesCircle className="text-[#fb4934]" />}
            {tech || 'NBN'} NBN Availability
          </h2>
          <span className={`px-2.5 py-0.5 rounded text-xs font-bold ${pass ? 'bg-[#b8bb26] text-[#1d2021]' : 'bg-[#fb4934] text-[#1d2021]'}`}>
            {detail.result || result?.status || '—'}
          </span>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-10 p-5">
          <div>
            <Row k="Result" v={detail.result} tone={pass ? 'good' : 'warn'} />
            <Row k="Service Type" v={TECH_NAMES[tech] || detail.serviceType} />
            <Row k="Availability" v={pass ? `NBN ${tech || ''}`.trim() : (result?.message || 'Not serviceable')} tone={pass ? undefined : 'warn'} />
            <Row k="Service Class" v={detail.serviceClass} />
            <Row k="CSA ID" v={detail.csa} />
            <Row k="Zone" v={detail.zone} />
            <Row k="Copper Disconnection" v={fmtDate(detail.copperDisconnectionDate)} />
            <Row
              k="Min ThroughPut"
              v={minThroughput || <span className="text-[#665c54]">Not returned by Vocus</span>}
              hint="The WSM SQ response has no minimum-throughput param — the vendor portal derives it."
            />
            <Row
              k="Max Available Speed"
              v={maxSpeed}
              hint="Derived here from the returned copper-pair / NTD speed ranges, not a single Vocus field."
            />
            <Row k="Additional Information" v={detail.notes?.length ? detail.notes.join(' ') : null} tone={detail.notes?.length ? 'warn' : 'muted'} />
            <Row k="Activation date" v={fmtDate(detail.activationDate) || 'N/A'} />
            <Row k={`${tech || 'NBN'} Higher Speed Tier`} v={higherSpeedTier || 'Not Supported'} />
            <Row k="Alternative Technology" v={altTech} />
          </div>

          <div>
            <Row k="Connection Type" v={detail.connectionType} />
            <Row
              k="Location ID"
              v={locationId || result?.nbnLocationId || result?.directoryId}
              hint="The LOC this qualification ran against."
            />
            <Row
              k="Address"
              v={addressLong || result?.addressLong}
              hint="Vocus's own text for that LOC, read from the same address record as the Location ID above — they cannot disagree. FIBRE::Get QUALIFY returns no address of its own."
            />
            <Row
              k="New Development Charges"
              v={detail.developmentCharge == null ? '—' : detail.developmentCharge ? 'Applicable — $300 nbn New Development Charge' : 'Not Applicable'}
              tone={detail.developmentCharge ? 'warn' : undefined}
            />
            <Row k="CVC ID" v={detail.cvcId} />
            <Row k="Subsequent Install Charge" v={subsequentInstall?.text} tone={subsequentInstall?.tone} />
            {detail.stag && <Row k="S-TAG" v={detail.stag} />}
            {detail.primaryTechnology && <Row k="Primary Technology" v={detail.primaryTechnology} />}
            {detail.battery != null && <Row k="Battery Backup" v={detail.battery ? 'Available' : 'Not available'} />}
            {detail.hfcSelfInstall != null && <Row k="HFC Self Install" v={detail.hfcSelfInstall ? 'Eligible' : 'Not eligible'} />}
            <Row
              k="Traffic Classes"
              v={Object.entries(detail.trafficClass || {})
                .filter(([, on]) => on)
                .map(([k]) => k.toUpperCase())
                .join(', ') || null}
            />
            {detail.coat?.rtcDate && <Row k="COAT / RTC date" v={fmtDate(detail.coat.rtcDate)} />}
          </div>
        </div>
      </div>

      {/* ── Copper pairs (CPI) — FTTC / FTTN / FTTB ── */}
      {pairs.length > 0 && (
        <div className="bg-[#282828] border border-[#3c3836] rounded-xl overflow-hidden">
          <div className="px-5 py-3 bg-[#1d2021] border-b border-[#3c3836] flex items-center justify-between flex-wrap gap-2">
            <h3 className="text-sm font-semibold text-[#ebdbb2]">Copper pairs at this premise ({pairs.length})</h3>
            <span className="text-[11px] text-[#a89984]">
              <span className="text-[#fb4934]">Line In Use</span> = an active NBN service already runs on that pair
            </span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-wide text-[#a89984] border-b border-[#3c3836]">
                  <th className="px-4 py-2 font-semibold">CPI ID</th>
                  <th className="px-4 py-2 font-semibold">POTS Match</th>
                  <th className="px-4 py-2 font-semibold">Copper Pair Status</th>
                  <th className="px-4 py-2 font-semibold">Status</th>
                  <th className="px-4 py-2 font-semibold">Class</th>
                  <th className="px-4 py-2 font-semibold">Max Download</th>
                  <th className="px-4 py-2 font-semibold">Max Upload</th>
                  <th className="px-4 py-2 font-semibold">TC2 Speed</th>
                  <th className="px-4 py-2 font-semibold">EPID</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#3c3836]">
                {pairs.map((c, i) => {
                  const inUse = /in use|active/i.test(String(c.nbnServiceStatus || ''));
                  return (
                    <tr key={c.copperPairId || i} className="hover:bg-[#32302f]">
                      <td className="px-4 py-2.5 font-mono text-[#ebdbb2] whitespace-nowrap">{c.copperPairId || '—'}</td>
                      <td className="px-4 py-2.5 text-[#a89984]">{c.potsMatch == null ? '—' : c.potsMatch ? 'Match' : 'No Match'}</td>
                      <td className="px-4 py-2.5 text-[#a89984]">{c.status || '—'}</td>
                      <td className={`px-4 py-2.5 ${inUse ? 'text-[#fb4934]' : 'text-[#a89984]'}`}>{c.nbnServiceStatus || 'N/A'}</td>
                      <td className={`px-4 py-2.5 ${inUse ? 'text-[#fb4934]' : 'text-[#a89984]'}`}>{c.serviceClass || '—'}</td>
                      <td className="px-4 py-2.5 text-[#a89984] whitespace-nowrap">{c.downloadSpeed ? `${c.downloadSpeed} Mbps` : '—'}</td>
                      <td className="px-4 py-2.5 text-[#a89984] whitespace-nowrap">{c.uploadSpeed ? `${c.uploadSpeed} Mbps` : '—'}</td>
                      <td className="px-4 py-2.5 text-[#a89984] whitespace-nowrap">{c.tc2Speed || '—'}</td>
                      <td className="px-4 py-2.5 font-mono text-[11px] text-[#928374]">{c.epid || '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {pairs.every((c) => !c.tc2Speed) && (
            <p className="px-4 py-2 text-[11px] text-[#665c54] border-t border-[#3c3836]">
              TC2 Speed is blank until <code>check-vocus-availability</code> is redeployed — the parser only started
              reading <code>TC2Speed</code> from <code>CopperPairRecord</code> in this change.
            </p>
          )}
        </div>
      )}

      {/* ── NTDs / UNI-D ports — FTTP / HFC / FW ── */}
      {ntds.length > 0 && (
        <div className="bg-[#282828] border border-[#3c3836] rounded-xl overflow-hidden">
          <div className="px-5 py-3 bg-[#1d2021] border-b border-[#3c3836] flex items-center justify-between flex-wrap gap-2">
            <h3 className="text-sm font-semibold text-[#ebdbb2] flex items-center gap-2">
              <FaNetworkWired className="text-[#83a598]" /> Network termination devices ({ntds.length})
            </h3>
            <span className="text-[11px] text-[#a89984] flex items-center gap-3">
              <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-[#b8bb26] inline-block" /> Port free</span>
              <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-[#d79921] inline-block" /> Port in use</span>
            </span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-wide text-[#a89984] border-b border-[#3c3836]">
                  <th className="px-4 py-2 font-semibold">NTD ID</th>
                  <th className="px-4 py-2 font-semibold">NTD Detail</th>
                  <th className="px-4 py-2 font-semibold">Available Capacity</th>
                  <th className="px-4 py-2 font-semibold">Data Ports</th>
                  <th className="px-4 py-2 font-semibold">Battery</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#3c3836]">
                {ntds.map((n, i) => (
                  <tr key={n.ntdId || i} className="hover:bg-[#32302f]">
                    <td className="px-4 py-2.5 font-mono text-[#ebdbb2] whitespace-nowrap">{n.ntdId || '—'}</td>
                    <td className="px-4 py-2.5 text-[#a89984] whitespace-nowrap">{n.ntdDetail || '—'}</td>
                    <td className="px-4 py-2.5 text-[#b8bb26] whitespace-nowrap">
                      ↓ {n.remainingDownload ?? '—'} ↑ {n.remainingUpload ?? '—'} Mbps
                      {(n.downloadSpeed || n.uploadSpeed) && (
                        <div className="text-[11px] text-[#928374]">of ↓ {n.downloadSpeed ?? '—'} ↑ {n.uploadSpeed ?? '—'}</div>
                      )}
                    </td>
                    <td className="px-4 py-2.5">
                      {(n.ports || []).filter((p) => p.portType !== 'VOICE').length > 0
                        ? n.ports.filter((p) => p.portType !== 'VOICE').map((p) => <PortPill key={`${n.ntdId}-${p.portNumber}`} port={p} />)
                        : <span className="text-[#928374]">—</span>}
                    </td>
                    <td className="px-4 py-2.5 text-[#a89984]">{n.battery == null ? '—' : n.battery ? 'Yes' : 'No'}</td>
                  </tr>
                ))}
                {(detail.orphanPorts || []).filter((p) => p.portType !== 'VOICE').length > 0 && (
                  <tr>
                    <td className="px-4 py-2.5 font-mono text-[#928374]">{detail.orphanPorts[0].ntdId || 'unlisted NTD'}</td>
                    <td className="px-4 py-2.5 text-[#928374]">No NTD record</td>
                    <td className="px-4 py-2.5 text-[#928374]">—</td>
                    <td className="px-4 py-2.5">
                      {detail.orphanPorts.filter((p) => p.portType !== 'VOICE').map((p) => (
                        <PortPill key={`orphan-${p.ntdId}-${p.portNumber}`} port={p} />
                      ))}
                    </td>
                    <td className="px-4 py-2.5 text-[#928374]">—</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {pairs.length === 0 && ntds.length === 0 && allPorts.length === 0 && (
        <div className="p-4 rounded-xl bg-amber-900/25 border border-amber-700/60 text-amber-200 text-sm flex items-start gap-2">
          <FaExclamationTriangle className="mt-0.5 shrink-0" />
          Vocus returned no copper-pair or NTD records for this LOC. Normal for some technologies (fixed wireless,
          satellite) and for greenfield premises with no equipment fitted yet.
        </div>
      )}
    </div>
  );
}
